import { createContext, useContext, useMemo } from "react";

import type { Suggestion } from "@/components/suggestions/types";
import type { PropsWithChildren } from "react";

type SuggestionsListProviderProps = {
  /** The root menu's element id, which item ids derive from. */
  menuId: string;
  onSelect: (suggestion: Suggestion, e?: KeyboardEvent, isSelectedManually?: boolean) => void;
  onActiveIndexChange: (listId: string | null, index: number, fromKeyboard?: boolean) => void;
};

const SuggestionsListContext = createContext<SuggestionsListProviderProps | null>(null);

export function SuggestionsListProvider({
  menuId,
  onSelect,
  onActiveIndexChange,
  children,
}: PropsWithChildren<SuggestionsListProviderProps>) {
  const value = useMemo(
    () => ({ menuId, onSelect, onActiveIndexChange }),
    [menuId, onSelect, onActiveIndexChange],
  );

  return (
    <SuggestionsListContext.Provider value={value}>{children}</SuggestionsListContext.Provider>
  );
}

/**
 * The element id of the item at `index` in list `listId` (`null` for the root list), so an input
 * that keeps focus can point `aria-activedescendant` at the active item.
 */
export function suggestionOptionId(menuId: string, listId: string | null, index: number): string {
  return `${menuId}-${listId ?? "root"}-${index}`;
}

/** The element id of the submenu opened from a nested option, which lives in a portal. */
export function suggestionSubmenuId(menuId: string, listId: string): string {
  return `${menuId}-${listId}-submenu`;
}

export function useSuggestionsListContext() {
  const context = useContext(SuggestionsListContext);

  if (!context) {
    throw new Error("useSuggestionsListContext must be used within SuggestionsListProvider");
  }

  return context;
}
